import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSlideNavigation, SlideIcon, LanguageSwitcher, trackEvent } from '../../shared';
import { X, ChevronLeft, ChevronRight, CheckCircle } from 'lucide-react';
import './Training.css';

/**
 * TrainingPresenter - Unified slide presentation component
 *
 * @param {Array} slides - Array of slide objects (see MODULE.md for format)
 * @param {string} moduleName - Module identifier for analytics
 */
const TrainingPresenter = ({ slides, moduleName }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const startTimeRef = useRef(Date.now());

  const {
    currentSlide,
    isAnimating,
    progress,
    isFirstSlide,
    isLastSlide,
    nextSlide,
    prevSlide,
  } = useSlideNavigation(slides.length, () => navigate('/'));

  const currentSlideData = slides[currentSlide];

  // Track each slide view
  useEffect(() => {
    trackEvent('slide_viewed', {
      module: moduleName,
      slideNumber: currentSlide + 1,
      totalSlides: slides.length,
      slideTitle: currentSlideData?.title,
    });
  }, [currentSlide, moduleName, slides.length, currentSlideData?.title]);

  const handleFinish = () => {
    const durationSeconds = Math.round((Date.now() - startTimeRef.current) / 1000);
    trackEvent('training_completed', {
      module: moduleName,
      language: i18n.language,
      durationSeconds,
      totalSlides: slides.length,
    });
    navigate('/');
  };

  return (
    <div className="training-container">
      <div className="training-header">
        <button
          className="training-close"
          onClick={() => navigate('/')}
          aria-label={t('training.closeTraining')}
        >
          <X size={18} />
        </button>
        <div className="training-progress-bar">
          <div
            className="training-progress-fill"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="training-slide-counter">
          {t('training.slideCounter', { current: currentSlide + 1, total: slides.length })}
        </div>
        <LanguageSwitcher className="training-lang-switcher" />
      </div>

      <div className={`training-slide ${isAnimating ? 'animating' : ''}`}>
        <div className="slide-content">
          <div className="slide-icon">
            <SlideIcon name={currentSlideData.icon} size={48} />
          </div>
          <h1 className="slide-title">{currentSlideData.title}</h1>
          <p className="slide-text">{currentSlideData.content}</p>

          {currentSlideData.tips && (
            <div className="slide-tips">
              <h3>{t('training.keyTips')}</h3>
              <ul>
                {currentSlideData.tips.map((tip, index) => (
                  <li key={index} className="tip-item">
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="training-navigation">
        <button
          className="nav-button prev-button"
          onClick={prevSlide}
          disabled={isFirstSlide}
        >
          <ChevronLeft size={18} />
          {t('training.previous')}
        </button>

        {isLastSlide ? (
          <button
            className="nav-button finish-button"
            onClick={handleFinish}
          >
            <CheckCircle size={18} />
            {t('training.finishTraining')}
          </button>
        ) : (
          <button className="nav-button next-button" onClick={nextSlide}>
            {t('training.next')}
            <ChevronRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
};

export default TrainingPresenter;
